import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { apiUrls } from '../constants/apiurls';
import projects from '../redux/modules/projects';
import { removeCandidates, toCandidatesPayload, updateCandidates } from './pipelineCandidates';

const projectListApi = projects.list();
const projectFormApi = projects.form();

// One key per project so the board, the bin and the projects dropdown share one saving state.
export const projectCandidatesCacheKey = (projectId) => `project-candidates-${projectId}`;

export const useProjectCandidates = (projectId) => {
  const mounted = useRef(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [optimisticCandidates, setOptimisticCandidates] = useState(null);

  const {
    data: projectData,
    isLoading: isLoadingProject,
    isFetching: isFetchingProject,
    error: errorProject,
    refetch,
  } = projectListApi.useProjectlistQuery(
    { params: { id: projectId }, extendedPath: apiUrls.getProject() },
    { skip: !projectId },
  );

  const [updateProject, updateProjectData] = projectFormApi.useProjectupdateMutation({
    fixedCacheKey: projectCandidatesCacheKey(projectId),
  });

  const isSaving = updateProjectData.isLoading || isFetchingProject;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isFetchingProject) {
      setOptimisticCandidates(null);
    }
  }, [isFetchingProject]);

  useEffect(() => {
    setSelectedIds([]);
    setOptimisticCandidates(null);
  }, [projectId]);

  const saveCandidates = async (ids, candidates) => {
    if (isSaving || !projectData?.data?.id) return;
    setOptimisticCandidates(candidates);
    try {
      await updateProject({
        id: projectData.data.id,
        projects_pipeline_stages_users: toCandidatesPayload(candidates),
      }).unwrap();
      if (mounted.current) {
        setSelectedIds((selected) => selected.filter((id) => !ids.includes(id)));
      }
      toast.success('Candidates updated');
    } catch (error) {
      if (mounted.current) {
        setOptimisticCandidates(null);
      }
      toast.error(error.data?.errors?.[0] || 'Couldn’t update candidates. Please try again.');
    }
  };

  const move = (ids, stageId) => {
    saveCandidates(
      ids,
      updateCandidates(projectData.data.projects_pipeline_stages_users, ids, {
        pipeline_stage_id: stageId,
        binned: 0,
      }),
    );
  };

  const setBinned = (ids, binned) => {
    saveCandidates(
      ids,
      updateCandidates(projectData.data.projects_pipeline_stages_users, ids, { binned }),
    );
  };

  const remove = (ids, { confirmed = false } = {}) => {
    const count = `${ids.length} ${ids.length === 1 ? 'candidate' : 'candidates'}`;
    if (confirmed || window.confirm(`Remove ${count} from this project? This cannot be undone.`)) {
      saveCandidates(ids, removeCandidates(projectData.data.projects_pipeline_stages_users, ids));
    }
  };

  const toggleSelected = (cardId) => {
    setSelectedIds((selected) =>
      selected.includes(cardId) ? selected.filter((id) => id !== cardId) : [...selected, cardId],
    );
  };

  return {
    projectData,
    isLoadingProject,
    errorProject,
    refetch,
    candidates: optimisticCandidates || projectData?.data?.projects_pipeline_stages_users,
    isSaving,
    selectedIds,
    toggleSelected,
    selectIds: setSelectedIds,
    clearSelection: () => setSelectedIds([]),
    move,
    setBinned,
    remove,
  };
};
